import { pdfWorkerUrl } from "@/lib/pdfjsWorker"
import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type {
  IHighlight,
  PdfHighlighterInstance,
  ScrollToHighlight,
} from "react-pdf-highlighter"
import { PdfLoader } from "react-pdf-highlighter"
import "react-pdf-highlighter/dist/style.css"
import "./pdfHighlighterOverrides.css"

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { BoundingRegion } from "@/types/questionnaire/models"

import {
  createHighlightFromBoundingRegions,
  hasBoundingRegions,
} from "./boundingRegionHighlight"
import { PdfDocumentLayer } from "./PdfDocumentLayer"
import {
  createHighlightFromMatch,
  findTextInPage,
  type PdfViewportInfo,
  type TextMatch,
} from "./pdfHighlightUtils"
import { PdfViewerControls } from "./PdfViewerControls"
import { clampPageNumber, getNextZoomLevel } from "./pdfViewerMath"

interface PdfHighlightViewerProps {
  fileUrl: string
  initialPage?: number
  searchText?: string
  boundingRegions?: BoundingRegion[]
  onTextSelected?: (page: number, text: string) => void
}

export const PdfHighlightViewer: FC<PdfHighlightViewerProps> = ({
  fileUrl,
  initialPage = 1,
  searchText,
  boundingRegions,
  onTextSelected,
}) => {
  const { translate } = useTranslation()
  const [highlights, setHighlights] = useState<IHighlight[]>([])
  const scrollViewerToRef = useRef<ScrollToHighlight | null>(null)
  const pdfDocumentRef = useRef<PDFDocumentProxy | null>(null)
  const pdfHighlighterRef = useRef<PdfHighlighterInstance | null>(null)
  const [currentPage, setCurrentPage] = useState(initialPage ?? 1)
  const [totalPages, setTotalPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const pendingPageRef = useRef<number | null>(null)
  const lastSearchContextRef = useRef<{
    fileUrl: string
    page: number
    text: string
  } | null>(null)
  const lastMatchRef = useRef<{ match: TextMatch; pageNumber: number }[]>([])
  const highlightIdRef = useRef<string | null>(null)
  const normalizedPropSearch = searchText?.trim()
  const resolvedSearchText =
    normalizedPropSearch && normalizedPropSearch.length > 0
      ? normalizedPropSearch
      : ""
  const hasBbox = hasBoundingRegions(boundingRegions)
  const shouldAttemptSearch = Boolean(resolvedSearchText) || hasBbox

  // Reset state when file changes
  useEffect(() => {
    const targetPage = initialPage && initialPage > 0 ? initialPage : 1
    pendingPageRef.current = targetPage
    setCurrentPage(targetPage)
    setHighlights([])
    setTotalPages(0)
    pdfDocumentRef.current = null
    lastSearchContextRef.current = null
    lastMatchRef.current = []
    highlightIdRef.current = null
  }, [fileUrl, initialPage])

  useEffect(() => {
    setHighlights([])
    lastSearchContextRef.current = null
    lastMatchRef.current = []
    highlightIdRef.current = null
  }, [resolvedSearchText])

  // Clamp current page to valid range
  useEffect(() => {
    if (totalPages <= 0) {
      return
    }

    setCurrentPage((prev) => {
      const next = clampPageNumber(prev, totalPages)
      if (next !== prev) {
        pendingPageRef.current = next
      }
      return next
    })
  }, [totalPages])

  // Navigate to page when programmatically requested
  useEffect(() => {
    if (pendingPageRef.current === null) {
      return
    }

    let cancelled = false

    const applyNavigation = () => {
      if (cancelled) {
        return
      }

      const targetPage = pendingPageRef.current
      if (targetPage === null) {
        return
      }

      const viewer = pdfHighlighterRef.current?.viewer
      if (viewer?.currentPageNumber === targetPage) {
        pendingPageRef.current = null
        return
      }

      if (viewer) {
        viewer.currentPageNumber = targetPage
        pendingPageRef.current = null
        return
      }

      window.requestAnimationFrame(applyNavigation)
    }

    applyNavigation()

    return () => {
      cancelled = true
    }
  }, [currentPage])

  // Update scale when it changes
  useEffect(() => {
    const viewer = pdfHighlighterRef.current?.viewer
    if (viewer && viewer.currentScale !== scale) {
      viewer.currentScale = scale
    }
  }, [scale])

  useEffect(() => {
    if (!shouldAttemptSearch) {
      return
    }

    const desiredInitialPage = initialPage && initialPage > 0 ? initialPage : 1
    const targetPage =
      totalPages > 0
        ? clampPageNumber(desiredInitialPage, totalPages)
        : desiredInitialPage

    const currentContext = {
      fileUrl,
      page: targetPage,
      text: resolvedSearchText,
    }

    const lastContext = lastSearchContextRef.current
    const isSameContext =
      !!lastContext &&
      lastContext.fileUrl === currentContext.fileUrl &&
      lastContext.page === currentContext.page &&
      lastContext.text === currentContext.text

    if (
      currentPage !== targetPage ||
      isSameContext ||
      !pdfDocumentRef.current ||
      totalPages === 0
    ) {
      return
    }

    if (targetPage < 1 || targetPage > totalPages) {
      return
    }

    let cancelled = false
    let rafId: number | null = null
    let detachBus: (() => void) | null = null

    const waitForViewport = async (): Promise<PdfViewportInfo | null> => {
      const nextFrame = () =>
        new Promise<void>((resolve) => {
          rafId = window.requestAnimationFrame(() => {
            rafId = null
            resolve()
          })
        })

      while (!cancelled) {
        const viewer = pdfHighlighterRef.current?.viewer
        if (!viewer) {
          await nextFrame()
          continue
        }

        const pageView = viewer.getPageView?.(targetPage - 1)
        const viewport = pageView?.viewport as PdfViewportInfo | undefined
        const isTextLayerReady = pageView?.textLayer?.renderingDone

        if (viewport && isTextLayerReady) {
          return viewport
        }

        const eventBus = viewer.eventBus
        if (eventBus?.on) {
          const viewportFromEvent = await new Promise<PdfViewportInfo | null>(
            (resolve) => {
              const handleTextLayer = (event: { pageNumber?: number }) => {
                if (event?.pageNumber !== targetPage) {
                  return
                }
                eventBus.off?.("textlayerrendered", handleTextLayer)
                detachBus = null
                const readyView = viewer.getPageView?.(targetPage - 1)
                resolve(
                  (readyView?.viewport as PdfViewportInfo | undefined) ?? null,
                )
              }

              detachBus = () =>
                eventBus.off?.("textlayerrendered", handleTextLayer)
              eventBus.on("textlayerrendered", handleTextLayer)
            },
          )

          if (viewportFromEvent) {
            return viewportFromEvent
          }
        } else {
          await nextFrame()
        }
      }

      return null
    }

    const runSearch = async (viewportDimensions: PdfViewportInfo) => {
      lastSearchContextRef.current = currentContext

      try {
        if (!pdfDocumentRef.current) {
          return
        }

        // Try bounding-region highlight first (precise, no text search needed)
        if (hasBoundingRegions(boundingRegions)) {
          const bboxHighlight = createHighlightFromBoundingRegions(
            boundingRegions,
            targetPage,
            viewportDimensions,
            resolvedSearchText,
          )
          if (bboxHighlight) {
            const highlightId =
              highlightIdRef.current ?? `bbox-highlight-${Date.now()}`
            highlightIdRef.current = highlightId
            setHighlights([{ ...bboxHighlight, id: highlightId }])
            return
          }
        }

        // Fall back to text-search highlighting
        const page = await pdfDocumentRef.current.getPage(targetPage)
        const textContent = await page.getTextContent()
        const match = findTextInPage(resolvedSearchText, textContent, page)

        if (cancelled) {
          return
        }

        if (match) {
          const highlight = createHighlightFromMatch(
            match,
            targetPage,
            viewportDimensions,
          )
          const highlightId =
            highlightIdRef.current ?? `search-highlight-${Date.now()}`
          highlightIdRef.current = highlightId
          const highlightWithId = {
            ...highlight,
            id: highlightId,
          }

          lastMatchRef.current = [{ match, pageNumber: targetPage }]
          setHighlights([highlightWithId])
          return // Found on single page, done!
        }

        if (targetPage < pdfDocumentRef.current.numPages) {
          const nextPageNum = targetPage + 1
          const nextPage = await pdfDocumentRef.current.getPage(nextPageNum)
          const nextTextContent = await nextPage.getTextContent()
          const nextMatch = findTextInPage(
            resolvedSearchText,
            nextTextContent,
            nextPage,
          )

          if (cancelled) {
            return
          }

          if (nextMatch) {
            const viewer = pdfHighlighterRef.current?.viewer
            const nextPageView = viewer?.getPageView?.(nextPageNum - 1)
            const nextViewport = nextPageView?.viewport as
              | PdfViewportInfo
              | undefined

            if (nextViewport) {
              const highlight = createHighlightFromMatch(
                nextMatch,
                nextPageNum,
                nextViewport,
              )
              const highlightId =
                highlightIdRef.current ?? `search-highlight-${Date.now()}`
              highlightIdRef.current = highlightId
              const highlightWithId = {
                ...highlight,
                id: highlightId,
              }

              lastMatchRef.current = [
                { match: nextMatch, pageNumber: nextPageNum },
              ]
              setHighlights([highlightWithId])

              // Auto-scroll to next page
              pendingPageRef.current = nextPageNum
              setCurrentPage(nextPageNum)

              return
            }
          }
        }
      } catch {
        // Failing the search should not break the viewer; swallow errors silently.
      }
    }

    waitForViewport().then((viewport) => {
      if (!viewport || cancelled) {
        return
      }
      runSearch(viewport)
    })

    return () => {
      cancelled = true
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
      detachBus?.()
    }
  }, [
    currentPage,
    fileUrl,
    initialPage,
    resolvedSearchText,
    shouldAttemptSearch,
    totalPages,
    boundingRegions,
  ])

  useEffect(() => {
    if (!shouldAttemptSearch) {
      return
    }

    const storedMatches = lastMatchRef.current
    const hasBboxHighlight =
      hasBoundingRegions(boundingRegions) && highlights.length > 0

    if ((!storedMatches || storedMatches.length === 0) && !hasBboxHighlight) {
      return
    }

    let cancelled = false

    const recomputeHighlights = () => {
      if (cancelled) {
        return
      }

      const viewer = pdfHighlighterRef.current?.viewer
      if (!viewer) {
        window.requestAnimationFrame(recomputeHighlights)
        return
      }

      // Recompute bounding-region highlights on zoom
      if (hasBboxHighlight && boundingRegions) {
        const targetPage =
          initialPage && initialPage > 0
            ? clampPageNumber(initialPage, totalPages)
            : 1
        const pageView = viewer.getPageView?.(targetPage - 1)
        const viewport = pageView?.viewport as PdfViewportInfo | undefined
        if (viewport) {
          const bboxHighlight = createHighlightFromBoundingRegions(
            boundingRegions,
            targetPage,
            viewport,
            resolvedSearchText,
          )
          if (bboxHighlight) {
            setHighlights([
              {
                ...bboxHighlight,
                id: `bbox-highlight-zoom-${Date.now()}`,
              },
            ])
            return
          }
        }
      }

      // Recalculate text-search highlights for all pages
      const updatedHighlights: IHighlight[] = []

      for (const storedMatch of storedMatches ?? []) {
        const pageView = viewer.getPageView?.(storedMatch.pageNumber - 1)
        const viewport = pageView?.viewport as PdfViewportInfo | undefined

        if (!viewport) {
          window.requestAnimationFrame(recomputeHighlights)
          return
        }

        const updatedHighlight = createHighlightFromMatch(
          storedMatch.match,
          storedMatch.pageNumber,
          viewport,
        )

        updatedHighlights.push({
          ...updatedHighlight,
          id: `search-highlight-${Date.now()}-${storedMatch.pageNumber}`,
        })
      }

      setHighlights(updatedHighlights)
    }

    recomputeHighlights()

    return () => {
      cancelled = true
    }
  }, [scale, shouldAttemptSearch, boundingRegions])

  const handleViewerPageChange = useCallback((page: number) => {
    if (!Number.isFinite(page)) {
      return
    }

    setCurrentPage((prev) => {
      if (prev !== page) {
        return page
      }
      return prev
    })
  }, [])

  const handleZoomIn = useCallback(() => {
    setScale((prev) => getNextZoomLevel(prev, "in"))
  }, [])

  const handleZoomOut = useCallback(() => {
    setScale((prev) => getNextZoomLevel(prev, "out"))
  }, [])

  const requestPageChange = useCallback(
    (computeNext: (prev: number) => number) => {
      setCurrentPage((prev) => {
        const next = computeNext(prev)
        if (next !== prev) {
          pendingPageRef.current = next
        }
        return next
      })
    },
    [],
  )

  const handlePrevPage = useCallback(() => {
    if (currentPage <= 1) {
      return
    }
    requestPageChange((prev) => clampPageNumber(prev - 1, totalPages))
  }, [currentPage, requestPageChange, totalPages])

  const handleNextPage = useCallback(() => {
    if (!totalPages || currentPage >= totalPages) {
      return
    }
    requestPageChange((prev) => clampPageNumber(prev + 1, totalPages))
  }, [currentPage, requestPageChange, totalPages])

  // Memoize layer props to prevent unnecessary re-renders
  const layerProps = useMemo(
    () => ({
      scale,
      highlights,
      pdfHighlighterRef,
      scrollViewerToRef,
      setTotalPages,
      totalPages,
      pdfDocumentRef,
      onViewerPageChange: handleViewerPageChange,
      initialPage: currentPage,
      pendingPageRef,
      onTextSelected,
    }),
    [
      scale,
      highlights,
      totalPages,
      handleViewerPageChange,
      currentPage,
      onTextSelected,
    ],
  )

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Controls Bar */}
      <PdfViewerControls
        currentPage={currentPage}
        totalPages={totalPages}
        scale={scale}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />

      <div style={{ flex: 1, position: "relative" }}>
        {/* Without workerSrc, PdfLoader writes its unpkg default into the
            shared GlobalWorkerOptions on every load. */}
        <PdfLoader
          url={fileUrl}
          workerSrc={pdfWorkerUrl}
          beforeLoad={<div>{translate("PDF_VIEWER/LOADING")}</div>}
        >
          {(pdfDocument: PDFDocumentProxy) => (
            <PdfDocumentLayer
              key={`pdf-${fileUrl}`}
              pdfDocument={pdfDocument}
              {...layerProps}
            />
          )}
        </PdfLoader>
      </div>
    </div>
  )
}
